import { useNavigate } from 'react-router-dom';
import { clientApprovalTypeLabel } from '../../../lib/clientApprovals';
import {
  type PendingClientApproval, AT_RISK_COPY, clientApprovalVersionPath, formatDate, progressLabel,
} from '../../../lib/clientApprovalPortal';

/**
 * #3187 APPROVE.4 — "Needs your approval" (design v1.1 §5.4), from 3a's
 * GET /client-approval-requests/pending. One row per OPEN request on an
 * artefact's current version in which the viewer is a live approver:
 * deliverable, version, submitted date, progress ("2 of 3 approved"), and the
 * at-risk badge. Requests the viewer has already answered are listed apart,
 * as waiting on the others; only the unanswered ones count as needing them.
 */
export default function ClientApprovalsPendingList({ items }: { items: PendingClientApproval[] }) {
  const navigate = useNavigate();
  const toDecide = items.filter((i) => i.canRespond);
  const waiting = items.filter((i) => !i.canRespond);

  const row = (item: PendingClientApproval, actionable: boolean) => (
    <div
      key={item.requestId}
      data-testid={actionable ? 'client-approval-pending-item' : 'client-approval-waiting-item'}
      style={{
        padding: 16, borderRadius: 8, border: '1px solid var(--border)',
        borderLeft: `4px solid ${item.atRisk ? '#dc2626' : '#0d9488'}`, backgroundColor: 'var(--surface)', marginBottom: 12,
      }}
    >
      {item.atRisk && (
        <div data-testid="client-approval-at-risk" style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {AT_RISK_COPY}
        </div>
      )}
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{item.title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: actionable ? 12 : 0 }}>
        {clientApprovalTypeLabel(item.artefactType)} · version {item.version} · submitted {formatDate(item.openedAt)} ·{' '}
        <span data-testid="client-approval-progress">{progressLabel(item.approvedCount, item.approverCount)}</span>
        {!actionable && ' · you have responded'}
      </div>
      {actionable && (
        <button
          type="button"
          data-testid="client-approval-review"
          onClick={() => navigate(clientApprovalVersionPath(item.artefactId, item.requestId))}
          style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
        >
          Review and decide &rarr;
        </button>
      )}
    </div>
  );

  return (
    <div data-testid="client-approvals-pending">
      {toDecide.map((i) => row(i, true))}
      {waiting.length > 0 && (
        <div data-testid="client-approvals-waiting" style={{ marginTop: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6b7280)', marginBottom: 8 }}>Waiting for other approvers</div>
          {waiting.map((i) => row(i, false))}
        </div>
      )}
    </div>
  );
}
